import Vue from 'vue'
import Vuex from 'vuex'
import api from '@/api'
import Constants from '@/constants.js'
import utils from '@/utils'
import createPersistedState from 'vuex-persistedstate'
import * as Cookies from 'js-cookie'

Vue.use(Vuex)

const defaultFiltersForWorkersBase = {
  dateFrom: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime(),
  dateTo: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime() + 6 * 24 * 60 * 60 * 1000,
  workerNameOrPhoneFilter: null,
  workerTypeFilter: null,
  workerCategoryFilter: null,
  areBlockedWorkersVisible: false,
  areDismissWorkersVisible: false,
  showAddFilters: true
}

const store = new Vuex.Store({
  state: {
    auth: {
      authenticated: false,
      role: {
        code: '',
        name: ''
      },
      token: '',
      username: ''
    },
    addresses: [utils.getDefaultAddress()],
    addressesForPetrolService: [utils.getDefaultAddress()],
    distance: 0,
    distanceForPetrolService: 0,
    prepayments: [],
    groupedServices: [],
    additionalCategories: [],
    moverCategories: [],
    cashboxOperationTypes: [],
    directoryTabNumber: 0,
    regions: [],
    districts: [],
    dailyScheduleListForWorker: [],
    newDailyScheduleListForWorker: [],
    allOrdersFilter: {
      clientNameOrOrgNameFilter: '',
      clientPhoneFilter: '',
      dateFilterFrom: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).valueOf(),
      dateFilterTo: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 1).valueOf() - 1,
      statusesFilter: [],
      cashTypesFilter: [],
      serviceFilter: null,
      tabNum: 0
    },
    groupedServicesPromises: [],
    workersBaseFilters: Object.assign({}, defaultFiltersForWorkersBase),
    isWorkerBaseRequestedFromWorkerCard: false,
    waitingApiRequestsCount: 0
  },

  getters: {
    isWorkerBaseRequestedFromWorkerCard: state =>{
      return state.isWorkerBaseRequestedFromWorkerCard
    },
    waitingApiRequestsCount: state => {
      return state.waitingApiRequestsCount
    },
    defaultFiltersForWorkersBase: state =>{
      return defaultFiltersForWorkersBase
    },
    groupedServices: state => {
      return state.groupedServices
    },

    allAppointedWorkers: state => {
      let workers = state.groupedServices
        ? state.groupedServices
          .flatMap(gs => {
            return gs
              .filter(service => service.worker)
              .map(service => service.worker)
          })
          // distinct
          .filter((elem, pos, arr) =>
            arr.map(worker => worker.id).indexOf(elem.id) == pos) : []
      return workers
    },

    prepayments: state => {
      return state.prepayments
    },

    moverCategories: state => {
      return state.moverCategories
    },

    additionalCategories: state => {
      return state.additionalCategories
    },

    regions: state => {
      return state.regions
    },

    districts: state => {
      return state.districts
    },

    addresses: state => {
      return state.addresses
    },

    validAddresses: state => {
      return state.addresses
          .filter(address => address.lat != null && address.lng != null)
    },

    validAddressesCoords: (state, getters) => {
      // без комментариев, только координаты (чтобы watch не прокал лишний раз)
      return getters.validAddresses
          .map(address => {
            return {
              lat: address.lat,
              lng: address.lng
            }
          });
    },

    addressesForPetrolService: state => {
      return state.addressesForPetrolService
    },

    token: state => {
      let token = state.auth.token
      if (!token) { token = sessionStorage.getItem('shustrikoff_token') }
      return token
    },

    roleCode: state => {
      return state.auth.role.code
    },

    roleName: state => {
      return state.auth.role.name
    },

    username: state => {
      return state.auth.username
    },

    cashboxOperationTypes: state => {
      return state.cashboxOperationTypes
    },

    directoryTabNumber: state => {
      return state.directoryTabNumber
    },

    dailyScheduleListForWorker: state => {
      return state.dailyScheduleListForWorker
    },

    newDailyScheduleListForWorker: state => {
      return state.newDailyScheduleListForWorker
    },

    allOrdersFilter: state => {
      return state.allOrdersFilter
    },

    workersBaseFilters: state => {
      return state.workersBaseFilters    },

    groupedServicesPromises: state => {
      return state.groupedServicesPromises
    }

  },

  mutations: {
    waitingApiRequestsCount (state, inc) {
      if (inc) { state.waitingApiRequestsCount++ } else { state.waitingApiRequestsCount-- }
    },

    auth_success (state, response) {
      state.auth.authenticated = true
      state.auth.token = response.headers['auth-token']
      state.auth.role = {}
      state.auth.role.code = response.data.data.role.code
      state.auth.role.name = response.data.data.role.name
      state.auth.username = response.data.data.name.concat(' ', response.data.data.surname)
      sessionStorage.setItem('shustrikoff_authenticated', true)
      sessionStorage.setItem('shustrikoff_token', response.headers['auth-token'])
    },
    auth_error (state) {
      state.auth.authenticated = false
    },
    logout (state) {
      state.auth.authenticated = false
      state.auth.token = ''
      state.auth.role = {}
      sessionStorage.removeItem('shustrikoff_authenticated')
      sessionStorage.removeItem('shustrikoff_token')
    },
    updateAddresses (state, addresses) {
      state.addresses = addresses
    },
    updateAddress (state, payload) {
      state.addresses[payload.index] = payload.address
    },
    deleteAddress (state, index) {
      state.addresses.splice(index, 1)
    },
    updateAddressesForPetrolService (state, addresses) {
      state.addressesForPetrolService = addresses
    },
    updateAddressForPetrolService (state, payload) {
      state.addressesForPetrolService[payload.index] = payload.address
    },
    deleteAddressForPetrolService (state, index) {
      state.addressesForPetrolService.splice(index, 1)
    },

    setMap (state, map) {
      state.map = map
    },
    setDistance (state, distance) {
      state.distance = distance
    },

    setDistanceForPetrolService (state, distance) {
      state.distanceForPetrolService = distance
    },

    setDistanceForTruckingService (state, distance) {
      state.distanceForTruckingService = distance
    },

    updateGroupedServices (state, groupedServices) {
      utils.sortGroupedServicesByWorkerNullValue(groupedServices)
      state.groupedServices = groupedServices
    },

    updateGroupedServiceByIndex (state, payload) {
      let services = state.groupedServices[payload.index]

      if (payload.count > 0 && payload.count < services.length) {
        // Если количество услуг уменьшилось, отрезаем лишнее
        services = services.slice(0, payload.count)
      }

      let showWorkersWereRemovedAlert = false

      services = services
        .map(service => {
          let removeWorker = false

          if ((service.category !== undefined && payload.serviceData.category !== undefined && service.category.id !== payload.serviceData.category.id) ||
            // TODO удалять только рабочих, не подходящих по изменённому графику
            // А пока временное решение - снимать рабочих, если изменилось время заявки либо прогноз продолжительности
            (service.date !== undefined && payload.serviceData.date !== undefined && service.date !== payload.serviceData.date) ||
            (service.clientTime !== undefined && payload.serviceData.clientTime !== undefined && service.clientTime !== payload.serviceData.clientTime) ||
            (service.garbageDisposalDistrict !== undefined && service.garbageDisposalDistrict !== payload.serviceData.garbageDisposalDistrict) ||
            (service.inKzn !== undefined && service.inKzn !== payload.serviceData.inKzn) ||
            (service.addresses !== undefined &&
              // Проверяем равенство массивов координат адресов
              utils.checkArraysEquals(
                utils.getCoordsArrayOfAddresses(service.addresses)
                  .map(coords => coords[0] + ',' + coords[1]),
                utils.getCoordsArrayOfAddresses(payload.serviceData.addresses)
                  .map(coords => coords[0] + ',' + coords[1])) == false)) {
            removeWorker = true
          }

          Object.assign(service, payload.serviceData)
          if (removeWorker) {
            if (service.worker) {
              showWorkersWereRemovedAlert = true
            }

            utils.removeWorkerFromService(service)
          }
          return service
        })

      while (payload.count > services.length) {
        // Количество услуг увеличилось - добиваем список услуг копиями
        // первой услуги в списке (убирая назначенных рабочих)
        let freshService = Object.assign({}, services[0])
        freshService = Object.assign(freshService, {
          id: null,
          worker: null,
          status: 0,
          realStartTime: null,
          realEndTime: null,
          predictionTime: null,
          hasSmsSent: null,
          smsSentTime: null
        })
        services.push(freshService)
      }

      services = utils.sortServices(services)
      state.groupedServices[payload.index] = services
      // без конструкции с tmpArrays услуги не обновляются до изменения DOM,
      // в том числе с помощью функций $nextTick() и $forceUpdate()
      let tmpArrays = state.groupedServices
      state.groupedServices = []
      state.groupedServices = tmpArrays

      state.groupedServices = utils.sortGroupedServicesByWorkerNullValue(state.groupedServices)

      if (showWorkersWereRemovedAlert) {
        alert('Рабочие сняты с заказа. Назначьте заново!')
      }
    },

    /** Убрать тех рабочих с ГСМ и Суток простоя, которые не назначены ни на одну другую услугу */
    removeRedundantWorkersFromSecondaryServices(state) {
      let secondaryServiceWorkerIds = new Map();
      state.groupedServices.forEach(gs => {
        gs.filter(service => !utils.isServicePrimary(service) // второстепенные услуги
          && service.worker
          && service.worker.id > 0)
          .map(service => service.worker.id)
          .forEach(workerId => secondaryServiceWorkerIds.set(workerId, 0));
      });
      if (!secondaryServiceWorkerIds.size)
        return;

      state.groupedServices.forEach(gs => {
        gs.filter(service => utils.isServicePrimary(service) // основные услуги
          && service.worker
          && service.worker.id > 0)
          .map(service => service.worker.id)
          .forEach(workerId => {
            let workerCount = secondaryServiceWorkerIds.get(workerId);
            if (workerCount != undefined) {
              secondaryServiceWorkerIds.set(workerId, ++workerCount);
            }
          })
      });

      let workersRemovedFromOrdersServiceNames = [];
      state.groupedServices = state.groupedServices
        .map(gs => {
          return gs.map(service => {
            if (!utils.isServicePrimary(service)
              && service.worker
              && service.worker.id > 0
              && secondaryServiceWorkerIds.get(service.worker.id) == 0) {
              service.worker = null;

              if (service.type == 'OrderToPetrol' && workersRemovedFromOrdersServiceNames.indexOf('ГСМ') == -1) {
                workersRemovedFromOrdersServiceNames.push('ГСМ');
              }
              if (service.type == 'OrderToDowntime' && workersRemovedFromOrdersServiceNames.indexOf('Сутки простоя') == -1) {
                workersRemovedFromOrdersServiceNames.push('Сутки простоя');
              }
              if (service.type == 'OrderToAdditional' && workersRemovedFromOrdersServiceNames.indexOf(service.category.name) == -1) {
                workersRemovedFromOrdersServiceNames.push(service.category.name);
              }
            }

            return service;
          });
        });

      if (workersRemovedFromOrdersServiceNames.length > 0) {
        alert('Один или более рабочих были сняты с услуг'
            + (workersRemovedFromOrdersServiceNames.length == 1 ? 'и' : '')
            + ' '
            + workersRemovedFromOrdersServiceNames.join(', ')
            + ', т.к. они не остались ни на одной основной услуге!');
      }
    },

    updateWorkersInGroupedServiceByIndex (state, payload) {
      let services = state.groupedServices[payload.index]
      services.forEach((service, index) => {
        let selectedWorker = payload.workers ? (payload.workers[index] ? payload.workers[index] : null) : null
        Object.assign(service, {
          worker: selectedWorker
        })
      })
      services = utils.sortServices(services)
      state.groupedServices[payload.index] = services
    },

    deleteWorkerFromServiceByIndex (state, payload) {
      let services = state.groupedServices[payload.index]
      let servicesToDeleteWorkerFrom = services
        .filter(s => s.worker && s.worker.id == payload.workerId)
      if (servicesToDeleteWorkerFrom) {
        servicesToDeleteWorkerFrom.forEach(s => {
          Object.assign(s, {
            worker: null,
            status: 0,
            realStartTime: null,
            realEndTime: null,
            predictionTime: null,
            hasSmsSent: null,
            smsSentTime: null
          })
        })
        services = utils.sortServices(services)
        state.groupedServices[payload.index] = services
      }
    },

    addNewGroupedService (state, groupedService) {
      for (let gs of state.groupedServices) {
        let existedService = gs[0]
        let newService = groupedService[0]

        if (utils.compareGroupedServices(existedService, newService)) {
          if (!!newService.category.packing) {
            // Если упаковка - увеличиваем количество единиц упаковки
            existedService.itemCount = Number(existedService.itemCount || 0) + Number(newService.itemCount || 1);
          } else {
            // Иначе увеличиваем количество услуг
            gs.push(...groupedService)
          }
          state.groupedServices = utils.sortGroupedServicesByWorkerNullValue(state.groupedServices)
          return
        }
      }
      state.groupedServices.push(groupedService)
      state.groupedServices = utils.sortGroupedServicesByWorkerNullValue(state.groupedServices)
    },

    deleteGroupedServiceByIndex (state, index) {
      state.groupedServices.splice(index, 1)
    },

    updatePrepayments (state, prepayments) {
      state.prepayments = prepayments
    },

    updatePrepaymentByIndex (state, payload) {
      state.prepayments[payload.index] = payload.prepayment
    },

    addNewPrepayment (state, prepayment) {
      state.prepayments.push(prepayment)
    },

    deletePrepaymentByIndex (state, index) {
      if (index <= state.prepayments.length) {
        state.prepayments.splice(index, 1)
      }
    },

    updateMoverCategories (state, moverCategories) {
      state.moverCategories = moverCategories
    },

    updateAdditionalCategories (state, additionalCategories) {
      state.additionalCategories = additionalCategories
    },

    updateRegions (state, regions) {
      state.regions = regions
    },

    updateDistricts (state, districts) {
      state.districts = districts
    },

    updateAllOrdersFilter (state, allOrdersFilter) {
      state.allOrdersFilter = allOrdersFilter
    },

    updateWorkersBaseFilters (state, workersBaseFilters){
      Object.assign(state.workersBaseFilters, workersBaseFilters)
    },

    resetWorkersBaseFilters(state){
      state.workersBaseFilters = Object.assign({}, defaultFiltersForWorkersBase)
    },

    updateIsWorkerBaseRequestedFromWorkerCard (state, isWorkerBaseRequestedFromWorkerCard){
      state.isWorkerBaseRequestedFromWorkerCard = isWorkerBaseRequestedFromWorkerCard
    },

    updateDirectoryTabNumber (state, directoryTabNumber) {
      state.directoryTabNumber = directoryTabNumber
    },

    updateCashboxOperationTypes (state, cashboxOperationTypes) {
      state.cashboxOperationTypes = cashboxOperationTypes
    },

    updateDailyScheduleListForWorker (state, list) {
      state.dailyScheduleListForWorker = list
    },

    updateNewDailyScheduleListForWorker (state, list) {
      state.newDailyScheduleListForWorker = state.newDailyScheduleListForWorker.concat(list)
    },

    clearNewDailyScheduleListForWorker (state) {
      state.newDailyScheduleListForWorker = []
    },

    addNewGroupedServicesPromise (state, groupedServicePromise) {
      state.groupedServicesPromises.push(groupedServicePromise)
    },

    clearGroupedServicesPromises (state) {
      state.groupedServicesPromises = []
    }
  },

  actions: {
    waitingApiRequestsCount (context, inc) {
      return new Promise((resolve, reject) => {
        context.commit('waitingApiRequestsCount', inc)
        resolve()
      })
    },

    auth (context, payload) {
      return new Promise((resolve, reject) => {
        api
          .auth(payload.username, payload.password)
          .then(response => {
            context.commit('auth_success', response)
            resolve()
          })
          .catch((message) => {
            context.commit('auth_error')
            reject(message)
          })
      })
    },
    isAuthenticated (context, payload) {
      return new Promise((resolve, reject) => {
        let authenticated = context.state.auth.authenticated
        if (!authenticated) {
          authenticated = sessionStorage.getItem('shustrikoff_authenticated')
        }
        if (authenticated) {
          resolve()
        } else {
          reject()
        }
      })
    },
    logout (context) {
      api.logout()
      context.commit('logout')
    },
    calculateDistance (context) {
      return new Promise(async (resolve, reject) => {
        if (context.state.addresses.length === 1) {
          context.commit('setDistance', 0);
          resolve(0);
          return;
        }
        let points = context.state.addresses.map((address, i) => {
          return {
            type: (i === 0 || i === context.state.addresses.length - 1) ? 'wayPoint' : 'viaPoint',
            point: [address.lat, address.lng]
          }
        });
        ymaps.ready(() => {
          ymaps.route(points, {
            mapStateAutoApply: true
          }).then(route => {
            let distance = parseInt(route.properties.get('RouterRouteMetaData').Length.value / 1000);
            context.commit('setDistance', distance);
            resolve(distance);
          });
        });
      })
    },

    calculateDistanceForPetrolService (context) {
      return new Promise((resolve, reject) => {
        if (context.state.addressesForPetrolService.length === 1) {
          context.commit('setDistanceForPetrolService', 0)
          return
        }
        let points = context.state.addressesForPetrolService.map((address, i) => {
          return {
            type: (i === 0 || i === context.state.addressesForPetrolService.length - 1) ? 'wayPoint' : 'viaPoint',
            point: [address.lat, address.lng]
          }
        });
        ymaps.ready(() => {
          ymaps.route(points, {
            mapStateAutoApply: true
          }).then(function (route) {
            let distance = parseInt(route.properties.get('RouterRouteMetaData').Length.value / 1000)
            context.commit('setDistanceForPetrolService', distance)
            resolve(distance)
          })
        });
      })
    },

    /**
     * Пересчёт регионов и районов услуг при изменении списка адресов заявки
     */
    async recalculateRegionsAndDistricts({state, getters, commit, dispatch}, payload) {
      let newAddressList = payload.newAddressList;
      let oldAddressList = payload.oldAddressList;
      if (oldAddressList.length == 0
        || newAddressList.length > oldAddressList.length)
        return;

      let oldAddressCoords = oldAddressList
          .map(address => utils.getCoordsStringOfAddress(address));
      let coordsChanged = newAddressList
          .map(address => utils.getCoordsStringOfAddress(address))
          .filter(coords => oldAddressCoords.indexOf(coords) == -1);
      if (newAddressList.length == oldAddressList.length
          && coordsChanged.length == 0)
        return;

      let groupedServices = getters.groupedServices;
      let showWorkersWereRemovedAlert = false;

      // Услуги без своего отдельного списка адресов
      let servicesWithoutOwnAddresses = groupedServices
          .filter(gs => Constants.SERVICES_WITHOUT_OWN_ADDRESS_LIST.indexOf(gs[0].type) >= 0);

      if (servicesWithoutOwnAddresses.length > 0) {
        let regionDistrictData = await utils
            .spotRegionAndDistrictByAddresses(newAddressList, getters.regions, getters.districts);
        servicesWithoutOwnAddresses
            .reduce((gs1, gs2) => gs1.concat(gs2))
            .forEach(service => {
              Object.assign(service, regionDistrictData);
              if (!!service.worker) {
                utils.removeWorkerFromService(service);
                showWorkersWereRemovedAlert = true;
              }
            });
      }

      // Услуги с отдельным списком адресов, основанном на списке адресов самой заявки
      let servicesWithOwnAddressesDependantOnOrderAddresses = groupedServices
          .filter(gs => Constants.SERVICES_WITH_ADDRESS_LIST_DEPENDANT_ON_ORDER_ADDRESS_LIST.indexOf(gs[0].type) >= 0);

      if (servicesWithOwnAddressesDependantOnOrderAddresses.length > 0) {
        if (newAddressList.length > 0) {
          let orderAddressesStringIds = newAddressList
              .map(address => utils.getCoordsStringOfAddress(address));
          servicesWithOwnAddressesDependantOnOrderAddresses
              .forEach(async (gs) => {
                let addresses = gs[0].addresses;
                let reducedServiceAddresses = addresses
                    .filter(address => orderAddressesStringIds.indexOf(utils.getCoordsStringOfAddress(address)) >= 0);

                let addressesStringIds = addresses
                    .map(address => utils.getCoordsStringOfAddress(address));
                let reducedServiceAddressesStringIds = reducedServiceAddresses
                    .map(address => utils.getCoordsStringOfAddress(address));
                if (utils.checkArraysEquals(addressesStringIds, reducedServiceAddressesStringIds))
                  return;

                // Удалять рабочих, но только если был изменён/удалён адрес из списка адресов услуги
                let removeWorkers = ((gs[0].addresses || []).length != reducedServiceAddresses)
                    || reducedServiceAddressesStringIds
                        .filter(coords => coordsChanged.indexOf(coords) >= 0);

                gs.forEach(service => {
                  Object.assign(service, {
                    addresses: reducedServiceAddresses,
                  });
                  if (!!service.worker && removeWorkers) {
                    utils.removeWorkerFromService(service);
                    showWorkersWereRemovedAlert = removeWorkers;
                  }
                });

                let regionDistrictData = await utils
                    .spotRegionAndDistrictByAddresses(reducedServiceAddresses, getters.regions, getters.districts);
                gs.forEach(service => Object.assign(service, regionDistrictData))
              })
        } else {
          // Список адресов заявки пустой => должен быть пустым и список адресов услуги
          servicesWithOwnAddressesDependantOnOrderAddresses
              .reduce((gs1, gs2) => gs1.concat(gs2))
              .forEach(service => Object.assign(service, {
                addresses: [],
                startRegion: null,
                endRegion: null,
                startDistrict: null,
                endDistrict: null
              }))
        }
      }

      commit('updateGroupedServices', groupedServices);

      await dispatch('updateDistanceForServicesWithOwnDistance');

      if (showWorkersWereRemovedAlert)
        alert('Рабочие сняты с заказа. Назначьте заново!');
    },

    async updateDistanceForServicesWithOwnDistance({state, dispatch}) {
      let services = state.groupedServices
          .filter(gs => Constants.SERVICES_WITH_OWN_DISTANCE.indexOf(gs[0].type) >= 0)[0];
      if (!!services && services.length > 0) {
        let addresses = services[0].addresses;
        let distance = await dispatch('calculateDistanceForServiceWithOwnDistance', addresses);
        services.forEach(service => {
          service.distance = distance;
        })
      }
    },

    calculateDistanceForServiceWithOwnDistance (context, addresses) {
      return new Promise((resolve, reject) => {
        if (addresses.length === 1) {
          resolve(0)
          return
        }
        let points = addresses.map((address, i) => {
          return {
            type: (i === 0 || i === addresses.length - 1) ? 'wayPoint' : 'viaPoint',
            point: [address.lat, address.lng]
          }
        });
        ymaps.ready(() => {
          ymaps.route(points, {
            mapStateAutoApply: true
          }).then(function (route) {
            let distance = parseInt(route.properties.get('RouterRouteMetaData').Length.value / 1000);
            resolve(distance)
          })
        });
      })
    },

    updateAddresses (context, addresses) {
      return new Promise((resolve, reject) => {
        context.commit('updateAddresses', addresses)
        resolve()
      })
    },
    updateAddress (context, payload) {
      return new Promise((resolve, reject) => {
        context.commit('updateAddress', payload)
        resolve()
      })
    },
    deleteAddress (context, index) {
      return new Promise((resolve, reject) => {
        context.commit('deleteAddress', index)
        resolve()
      })
    },

    updateAddressesForPetrolService ({ commit, dispatch }, addresses) {
      return new Promise((resolve, reject) => {
        commit('updateAddressesForPetrolService', addresses)
        dispatch('calculateDistanceForPetrolService')
        resolve()
      })
    },
    updateAddressForPetrolService (context, payload) {
      return new Promise((resolve, reject) => {
        context.commit('updateAddressForPetrolService', payload)
        resolve()
      })
    },
    deleteAddressForPetrolService (context, index) {
      return new Promise((resolve, reject) => {
        context.commit('deleteAddressForPetrolService', index)
        resolve()
      })
    },

    deleteWorkerFromServiceByIndex ({ commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        commit('deleteWorkerFromServiceByIndex', payload)
        commit('removeRedundantWorkersFromSecondaryServices')
        resolve()
      })
    },

    addNewGroupedService (context, groupedService) {
      return new Promise((resolve, reject) => {
        context.commit('addNewGroupedService', groupedService)
        resolve()
      })
    },
    updateGroupedServiceByIndex ({ commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        commit('updateGroupedServiceByIndex', payload)
        commit('removeRedundantWorkersFromSecondaryServices')
        resolve()
      })
    },
    updateWorkersBaseFilters ({context, payload}) {
      return new Promise((resolve, reject) => {
        context.commit('updateWorkersBaseFilters', payload)
        resolve()
      })
    },
    resetWorkersBaseFilters({context}){
      return new Promise((resolve, reject) => {
        context.commit('resetWorkersBaseFilters')
      })
    },
    updateIsWorkerBaseRequestedFromWorkerCard ({context, payload}){
      return new Promise((resolve, reject) =>{
        context.commit('updateIsWorkerBaseRequestedFromWorkerCard', payload)
        resolve()
      })
    },
    removeRedundantWorkersFromSecondaryServices (context) {
      return new Promise((resolve, reject) => {
        context.commit('removeRedundantWorkersFromSecondaryServices')
        resolve()
      })
    }
  },

  plugins: [createPersistedState({
    key: 'auth',
    paths: ['auth'],
    storage: {
      getItem: key => Cookies.get(key),
      setItem: (key, value) =>
        Cookies.set(key, value, { expires: 3 }),
      removeItem: key => Cookies.remove(key)
    }
  })]
});

store.watch(
    (state, getters) => getters.validAddressesCoords,
    (newAddressList, oldAddressList) => {
      // Выполняем пересчёт начальных / конечных районов и регионов для услуг
      store.dispatch('recalculateRegionsAndDistricts', {
        newAddressList: newAddressList || [],
        oldAddressList: oldAddressList || []
      });
    });

export default store;
